/**
 * Centralized configuration for table column visibility control.
 *
 * This is the single source of truth for all table columns that can be toggled
 * by admins in the column visibility feature.
 *
 * Note: Actions and Checkbox columns are NOT included here as they are always visible.
 */

export interface TableColumnConfig {
  key: string;
  label: string;
}

export interface TableConfig {
  tableName: string;
  columns: TableColumnConfig[];
}

export const tableColumnsConfig: Record<string, TableConfig> = {
  sites: {
    tableName: "tableConfig.sites",
    columns: [
      { key: "id", label: "sites.columns.id" },
      { key: "name", label: "sites.columns.name" },
      { key: "location_text", label: "sites.columns.location" },
      { key: "updated_at", label: "sites.columns.lastUpdated" },
    ],
  },
  devices: {
    tableName: "tableConfig.devices",
    columns: [
      { key: "id", label: "devices.columns.id" },
      { key: "name", label: "devices.columns.name" },
      { key: "is_active", label: "devices.columns.active" },
      { key: "mobility_type", label: "devices.columns.mobility" },
      { key: "hardware_type", label: "devices.columns.hardware" },
      { key: "status_processing_type", label: "devices.columns.statusProcessing" },
      { key: "site", label: "devices.columns.site" },
      { key: "last_seen", label: "devices.columns.lastSeen" },
    ],
  },
  vehicles: {
    tableName: "tableConfig.vehicles",
    columns: [
      { key: "id", label: "vehicles.columns.id" },
      { key: "name", label: "vehicles.columns.name" },
      { key: "rfid", label: "vehicles.columns.rfid" },
      { key: "vehicle_model", label: "vehicles.columns.model" },
      { key: "site", label: "vehicles.columns.site" },
      { key: "assigned_driver", label: "vehicles.columns.driver" },
      { key: "maintenance", label: "vehicles.columns.maintenance" },
      { key: "updatedAt", label: "vehicles.columns.lastUpdated" },
    ],
  },
  vehicleModels: {
    tableName: "tableConfig.vehicleModels",
    columns: [
      { key: "id", label: "vehicleModels.columns.id" },
      { key: "name", label: "vehicleModels.columns.name" },
      { key: "brand", label: "vehicleModels.columns.brand" },
      { key: "model", label: "vehicleModels.columns.model" },
      { key: "size", label: "vehicleModels.columns.size" },
      { key: "updatedAt", label: "vehicleModels.columns.lastUpdated" },
    ],
  },
  items: {
    tableName: "tableConfig.items",
    columns: [
      { key: "createdAt", label: "items.columns.dateOfCreation" },
      { key: "external_id", label: "items.columns.externalId" },
      { key: "last_scan", label: "items.columns.lastScan" },
      { key: "item_type", label: "items.columns.type" },
      { key: "id", label: "items.columns.rfidTags" },
      { key: "status", label: "items.columns.status" },
      { key: "type", label: "items.columns.category" },
      { key: "location", label: "items.columns.location" },
      { key: "stolen", label: "items.columns.stolen" },
    ],
  },
  products: {
    tableName: "tableConfig.categories",
    columns: [
      { key: "name", label: "products.columns.name" },
      { key: "variant", label: "products.columns.variant" },
      { key: "cost", label: "products.columns.cost" },
      { key: "retail_price", label: "products.columns.retailPrice" },
      { key: "filling_capacity", label: "products.columns.fillingCapacity" },
      { key: "tare_weight", label: "products.columns.tareWeight" },
      { key: "number_of_items", label: "products.columns.numberOfItems" },
    ],
  },
  templates: {
    tableName: "tableConfig.templates",
    columns: [
      { key: "id", label: "common.id" },
      { key: "name", label: "templates.columns.name" },
      { key: "description", label: "templates.columns.description" },
      { key: "product", label: "templates.columns.category" },
      { key: "site", label: "templates.columns.site" },
      { key: "location", label: "templates.columns.location" },
      { key: "createdAt", label: "templates.columns.createdAt" },
    ],
  },
  rfidTags: {
    tableName: "tableConfig.rfidTags",
    columns: [
      { key: "rfid", label: "rfidTags.columns.rfid" },
      { key: "type", label: "rfidTags.columns.type" },
      { key: "linked_to", label: "rfidTags.columns.linkedTo" },
      { key: "last_scanned_by", label: "rfidTags.columns.scanner" },
      { key: "createdAt", label: "rfidTags.columns.createdAt" },
      { key: "updatedAt", label: "rfidTags.columns.lastUpdated" },
    ],
  },
  shipmentLogs: {
    tableName: "tableConfig.shipmentLogs",
    columns: [
      { key: "id", label: "shipments.columns.id" },
      { key: "label", label: "shipments.columns.label" },
      { key: "vehicle", label: "shipments.columns.vehicle" },
      { key: "items", label: "shipments.columns.items" },
      { key: "location", label: "shipments.columns.location" },
      { key: "scanner", label: "shipments.columns.scanner" },
      { key: "date", label: "shipments.columns.date" },
      { key: "updatedAt", label: "shipments.columns.lastUpdated" },
    ],
  },
};

/**
 * Get all available table names
 */
export const getTableNames = (): string[] => {
  return Object.keys(tableColumnsConfig);
};

/**
 * Get configuration for a specific table
 */
export const getTableConfig = (tableName: string): TableConfig | undefined => {
  return tableColumnsConfig[tableName];
};

/**
 * Get all column keys for a specific table
 */
export const getTableColumnKeys = (tableName: string): string[] => {
  const config = tableColumnsConfig[tableName];
  return config ? config.columns.map((col) => col.key) : [];
};
